// Step 5d Phase 3b — change-feed client (the DOWNLOAD half of sync).
//
// Calls GET /api/sync/changes and pages via next_cursor until caught up, returning the
// accumulated `tasks` change metadata + the head cursor the device is now caught up to.
// Like the rest of the offline lib it is FAILURE-TOLERANT: any feed error degrades to
// "no feed info" (headCursor = null) so the caller simply full-refetches — a feed problem
// must NEVER break the online app.
//
// The cursor is OPAQUE: stored + echoed verbatim, never parsed here (the server encodes a
// keyset position into it). The feed carries METADATA only — the caller re-fetches changed
// rows through the existing GET /api/bookings read path (one serialization path).

import { apiFetch } from '../api';

export interface FeedChange {
  id: string;          // audit_log row id
  record_id: string;   // the booking (tasks row) that changed
  action: string;      // INSERT | UPDATE | DELETE | …
  created_at: string;  // ISO
}

export interface ChangesSince {
  /** All changes strictly after the input cursor (empty for a baseline / when caught up). */
  changes: FeedChange[];
  /**
   * The head the device is now caught up to (store this as the scope cursor), or null when
   * the feed FAILED and must not be trusted — the caller should full-refetch and leave the
   * stored cursor unchanged. On success this is always a non-empty opaque string (the server
   * returns an epoch sentinel even when the tenant has no tasks audit rows yet).
   */
  headCursor: string | null;
}

interface ChangesResponse { changes: FeedChange[]; next_cursor: string; has_more: boolean }

// Backstop against an unbounded loop on a misbehaving server (each page is capped at the
// server's hard limit, so this is a generous ceiling, not an expected path).
const MAX_PAGES = 50;

export async function fetchChangesSince(cursor: string | null): Promise<ChangesSince> {
  try {
    const all: FeedChange[] = [];
    // null cursor → ask for the baseline head (server returns empty changes + current head).
    let next: string | null = cursor;
    for (let page = 0; page < MAX_PAGES; page++) {
      const q = next ? `?cursor=${encodeURIComponent(next)}` : '';
      const res = await apiFetch<ChangesResponse>(`/api/sync/changes${q}`);
      if (res.error || !res.data) return { changes: all, headCursor: null }; // degrade → full refetch
      const { changes, next_cursor, has_more } = res.data;
      all.push(...changes);
      next = next_cursor;
      if (!has_more) return { changes: all, headCursor: next };
    }
    // Hit the page ceiling — treat as untrusted so the caller full-refetches.
    return { changes: all, headCursor: null };
  } catch {
    return { changes: [], headCursor: null };
  }
}
